import { useCallback, useEffect, useRef, useState } from 'react';

import { USER_CHOICES } from 'contexts/VideoGridContext/layoutUtils';
import useLocalStorage from 'hooks/useLocalStorage/useLocalStorage';
import { isSafariBased } from 'utils/browsers';
import logger from 'utils/logging/faro';

export type PlaySoundOptions = {
  volume?: number;
};

const useSoundInitializer = () => {
  const [audioOutputId] = useLocalStorage(USER_CHOICES.audioOutputId, 'default');
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioCleanupRef = useRef<(() => void) | undefined>(undefined);

  /**
   * Initializes the audio element and sets its output device.
   *
   * Important:
   * - Without user gesture, it can only be called with an empty string to use the default device because of restrictions in some browsers (based on testing)
   * - Passing `null` for `outputId` will select the output device stored in local storage.
   *
   * @param {string | null} outputId - The device ID of the audio output to use, or `null` to select default.
   */
  const initializeAudio = useCallback(
    (outputId: string | null) => {
      logger.info('Initializing audio', { outputId, audioOutputId });

      audioCleanupRef.current?.();
      audioElementRef.current = new Audio();

      const handlePlay = () => setIsPlaying(true);
      const handlePauseOrEnd = () => setIsPlaying(false);

      audioElementRef.current?.addEventListener('play', handlePlay);
      audioElementRef.current?.addEventListener('pause', handlePauseOrEnd);
      audioElementRef.current?.addEventListener('ended', handlePauseOrEnd);

      audioCleanupRef.current = () => {
        logger.info('Running audio cleanup');
        audioElementRef.current?.removeEventListener('play', handlePlay);
        audioElementRef.current?.removeEventListener('pause', handlePauseOrEnd);
        audioElementRef.current?.removeEventListener('ended', handlePauseOrEnd);
      };

      if (!audioElementRef.current.setSinkId) return logger.error('No support for setSinkId when initializing audio');

      audioElementRef.current
        .setSinkId(outputId ?? audioOutputId)
        .catch((e) => logger.error('Error setting sink id', e));
    },
    [audioOutputId]
  );

  const playSound = useCallback((src: string, options?: PlaySoundOptions) => {
    if (!audioElementRef.current) return logger.error('No audio element to play sound');

    audioElementRef.current.src = src;
    audioElementRef.current.volume = options?.volume || 1;
    audioElementRef.current.play().catch((e) => logger.error('Error playing sound', e));
  }, []);

  // Unlock audio on gesture event
  useEffect(() => {
    if (audioElementRef.current) return;

    // NOTE: Safari and iOS are not supported until the livekit implements the support
    const initAudio = () => initializeAudio(isSafariBased() ? '' : null);
    const eventNames = ['click', 'touchstart', 'keydown'];

    eventNames.forEach((name) =>
      document.addEventListener(name, initAudio, { capture: true, passive: name === 'touchstart', once: true })
    );

    return () => {
      eventNames.forEach((event) => document.removeEventListener(event, initAudio, true));
    };
  }, [audioOutputId, initializeAudio]);

  useEffect(() => () => audioCleanupRef.current?.(), []);

  return { playSound, initializeAudio, isPlaying };
};

export default useSoundInitializer;
